/**
 * The receipt form's arithmetic (batch 8 T12c2, #3033): turns what the user typed per order line into the strict request body, or
 * into the problems that stop it. These are the T05 quantity rules as the server applies them; they are restated only so the
 * user sees them before sending. The server stays the authority (over-receipt against the counters, stale state, permissions), and
 * its refusal is shown when it differs. Nothing here touches money.
 *
 *  - `accepted + rejected + held = arrived`, all integers, `arrived` at least 1;
 *  - a reason exactly when units are rejected;
 *  - a line without a quality check (`qualityObserved: false`) carries no rejected or held units;
 *  - the quality check is an explicit choice per line: it is never defaulted to "checked".
 */

import {
  CLOSE_SHORT_REASON_MAX,
  MAX_QUANTITY,
  NOTE_MAX,
  REFERENCE_MAX,
  type CloseShortBody,
  type ReceiveGoodsBody,
  type ReceiveLineBody,
  type ReceivingLine,
  type RejectReason,
  type ResolveHoldBody,
} from './receivingTypes';

export type QualityChoice = 'unset' | 'observed' | 'unobserved';

export interface LineDraft {
  arrived: string;
  accepted: string;
  rejected: string;
  held: string;
  reason: '' | RejectReason;
  quality: QualityChoice;
  note: string;
}

export const emptyLineDraft = (): LineDraft => ({ arrived: '', accepted: '', rejected: '', held: '', reason: '', quality: 'unset', note: '' });

export type LineProblem =
  | 'arrivedInvalid'
  | 'acceptedRequired'
  | 'acceptedInvalid'
  | 'rejectedInvalid'
  | 'heldInvalid'
  | 'tooLarge'
  | 'splitMismatch'
  | 'reasonRequired'
  | 'qualityRequired'
  | 'unobservedUnits'
  | 'noteTooLong';

export interface LineEvaluation {
  /** The user entered something for this line (an arrival quantity of 1 or more, or any other field). */
  readonly touched: boolean;
  /** The line goes into the receipt: an arrival quantity above zero. */
  readonly included: boolean;
  readonly problems: readonly LineProblem[];
  /** Parsed quantities; `null` where the text is not a valid integer. */
  readonly arrived: number | null;
  readonly accepted: number | null;
  readonly rejected: number | null;
  readonly held: number | null;
  /** Set when the split does not add up: the sum of the three parts. */
  readonly splitSum: number | null;
}

const INTEGER = /^\d{1,9}$/;

export function parseQuantity(value: string): number | null {
  const trimmed = value.trim();
  if (!INTEGER.test(trimmed)) return null;
  return Number(trimmed);
}

export function evaluateLine(draft: LineDraft): LineEvaluation {
  const arrivedText = draft.arrived.trim();
  const arrived = arrivedText === '' ? null : parseQuantity(arrivedText);
  const touched =
    arrivedText !== '' ||
    draft.accepted.trim() !== '' ||
    draft.rejected.trim() !== '' ||
    draft.held.trim() !== '' ||
    draft.reason !== '' ||
    draft.note.trim() !== '';
  // An arrival of zero is "nothing arrived on this line": it is left out of the receipt, it is not an error.
  if (arrived === 0 && draft.accepted.trim() === '' && draft.rejected.trim() === '' && draft.held.trim() === '' && draft.reason === '') {
    return { touched: false, included: false, problems: [], arrived, accepted: null, rejected: null, held: null, splitSum: null };
  }
  if (!touched) return { touched: false, included: false, problems: [], arrived: null, accepted: null, rejected: null, held: null, splitSum: null };

  const problems: LineProblem[] = [];
  const unobserved = draft.quality === 'unobserved';
  const rejectedText = draft.rejected.trim();
  const accepted = draft.accepted.trim() === '' ? null : parseQuantity(draft.accepted);
  const rejected = rejectedText === '' ? 0 : parseQuantity(rejectedText);
  const held = draft.held.trim() === '' ? 0 : parseQuantity(draft.held);

  if (arrived === null || arrived < 1) problems.push('arrivedInvalid');
  if (draft.accepted.trim() === '') problems.push('acceptedRequired');
  else if (accepted === null) problems.push('acceptedInvalid');
  if (rejected === null) problems.push('rejectedInvalid');
  if (held === null) problems.push('heldInvalid');
  if ([arrived, accepted, rejected, held].some((value) => value !== null && value > MAX_QUANTITY)) problems.push('tooLarge');

  let splitSum: number | null = null;
  if (arrived !== null && arrived >= 1 && accepted !== null && rejected !== null && held !== null) {
    const sum = accepted + rejected + held;
    if (sum !== arrived) {
      problems.push('splitMismatch');
      splitSum = sum;
    }
  }
  if (rejected !== null && rejected > 0 && draft.reason === '') problems.push('reasonRequired');
  if (draft.quality === 'unset') problems.push('qualityRequired');
  if (unobserved && ((rejected ?? 0) > 0 || (held ?? 0) > 0)) problems.push('unobservedUnits');
  if (draft.note.trim().length > NOTE_MAX) problems.push('noteTooLong');
  return { touched: true, included: true, problems, arrived, accepted, rejected, held, splitSum };
}

export interface ReceiveFormInput {
  /** Drafts by order line id. */
  drafts: Readonly<Record<string, LineDraft>>;
  /** The order lines, in the order shown. */
  lines: readonly ReceivingLine[];
  deliveryNoteReference: string;
  note: string;
}

export interface ReceiveFormEvaluation {
  readonly byLine: ReadonlyMap<string, LineEvaluation>;
  readonly includedCount: number;
  readonly formProblems: readonly ('noLines' | 'referenceTooLong' | 'noteTooLong')[];
  readonly valid: boolean;
  /** The strict request body; `null` while anything is wrong. */
  readonly body: ReceiveGoodsBody | null;
}

export function evaluateReceiveForm(input: ReceiveFormInput): ReceiveFormEvaluation {
  const byLine = new Map<string, LineEvaluation>();
  const lines: ReceiveLineBody[] = [];
  let lineProblems = 0;
  for (const line of input.lines) {
    const draft = input.drafts[line.id] ?? emptyLineDraft();
    const evaluation = evaluateLine(draft);
    byLine.set(line.id, evaluation);
    if (!evaluation.included) continue;
    lineProblems += evaluation.problems.length;
    if (evaluation.problems.length > 0 || evaluation.arrived === null || evaluation.accepted === null || evaluation.rejected === null || evaluation.held === null) continue;
    const body: ReceiveLineBody = {
      lineId: line.id,
      quantityArrived: evaluation.arrived,
      quantityAccepted: evaluation.accepted,
      quantityRejected: evaluation.rejected,
      quantityHeld: evaluation.held,
      qualityObserved: draft.quality === 'observed',
    };
    if (evaluation.rejected > 0 && draft.reason !== '') body.rejectReason = draft.reason;
    if (draft.note.trim() !== '') body.note = draft.note.trim();
    lines.push(body);
  }
  const includedCount = [...byLine.values()].filter((entry) => entry.included).length;
  const formProblems: ('noLines' | 'referenceTooLong' | 'noteTooLong')[] = [];
  if (includedCount === 0) formProblems.push('noLines');
  if (input.deliveryNoteReference.trim().length > REFERENCE_MAX) formProblems.push('referenceTooLong');
  if (input.note.trim().length > NOTE_MAX) formProblems.push('noteTooLong');
  const valid = lineProblems === 0 && formProblems.length === 0 && lines.length === includedCount;
  let body: ReceiveGoodsBody | null = null;
  if (valid) {
    body = { lines };
    if (input.deliveryNoteReference.trim() !== '') body.deliveryNoteReference = input.deliveryNoteReference.trim();
    if (input.note.trim() !== '') body.note = input.note.trim();
  }
  return { byLine, includedCount, formProblems, valid, body };
}

/** Units still to arrive according to the counters the screen was given; `null` when it was not given the held/cancelled counters. */
export function remainingOf(line: ReceivingLine): number | null {
  if (line.quantityHeld === undefined || line.quantityCancelled === undefined) return null;
  return Math.max(line.quantity - line.quantityReceived - line.quantityHeld - line.quantityCancelled, 0);
}

// ---------------------------------------------------------------------------
// Hold decision
// ---------------------------------------------------------------------------

export interface HoldDraft {
  decision: '' | 'accept' | 'reject';
  quantity: string;
  reason: '' | RejectReason;
  note: string;
}

export const emptyHoldDraft = (): HoldDraft => ({ decision: '', quantity: '', reason: '', note: '' });

export type HoldProblem = 'decisionRequired' | 'quantityInvalid' | 'quantityExceedsHeld' | 'reasonRequired' | 'noteTooLong';

export function evaluateHold(draft: HoldDraft, held: number): { problems: HoldProblem[]; body: ResolveHoldBody | null } {
  const problems: HoldProblem[] = [];
  const quantity = parseQuantity(draft.quantity);
  if (draft.decision === '') problems.push('decisionRequired');
  if (quantity === null || quantity < 1) problems.push('quantityInvalid');
  else if (quantity > held) problems.push('quantityExceedsHeld');
  if (draft.decision === 'reject' && draft.reason === '') problems.push('reasonRequired');
  if (draft.note.trim().length > NOTE_MAX) problems.push('noteTooLong');
  if (problems.length > 0 || quantity === null || draft.decision === '') return { problems, body: null };
  const body: ResolveHoldBody = { decision: draft.decision, quantity };
  if (draft.decision === 'reject' && draft.reason !== '') body.rejectReason = draft.reason;
  if (draft.note.trim() !== '') body.note = draft.note.trim();
  return { problems, body };
}

// ---------------------------------------------------------------------------
// Close short
// ---------------------------------------------------------------------------

export interface CloseShortDraft {
  reason: string;
  scope: '' | 'all' | 'selected';
  selected: readonly string[];
}

export const emptyCloseShortDraft = (): CloseShortDraft => ({ reason: '', scope: '', selected: [] });

export type CloseShortProblem = 'reasonRequired' | 'reasonTooLong' | 'scopeRequired' | 'noneSelected';

export function evaluateCloseShort(draft: CloseShortDraft): { problems: CloseShortProblem[]; body: CloseShortBody | null } {
  const problems: CloseShortProblem[] = [];
  const reason = draft.reason.trim();
  if (reason === '') problems.push('reasonRequired');
  else if (reason.length > CLOSE_SHORT_REASON_MAX) problems.push('reasonTooLong');
  if (draft.scope === '') problems.push('scopeRequired');
  else if (draft.scope === 'selected' && draft.selected.length === 0) problems.push('noneSelected');
  if (problems.length > 0) return { problems, body: null };
  const body: CloseShortBody = { reason };
  if (draft.scope === 'selected') body.lineIds = [...draft.selected];
  return { problems, body };
}
